/**
 * Icon Library - Re-exports from Lucide React
 * Provides consistent icon API across the app
 */

import { cn } from '@/lib/utils'

// Re-export Lucide icons with consistent naming
export {
  // Action icons
  Pencil as EditIcon,
  Trash2 as DeleteIcon,
  Trash2 as TrashIcon,
  Plus as PlusIcon,
  Minus as MinusIcon,
  X as CloseIcon,
  Check as CheckIcon,
  RefreshCw as RefreshIcon,
  Copy as CopyIcon,
  Save as SaveIcon,
  RotateCcw as UndoIcon,
  RotateCw as RedoIcon,

  // Navigation icons
  Home as HomeIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  ChevronDown as ChevronDownIcon,
  ChevronUp as ChevronUpIcon,
  Menu as MenuIcon,
  MoreHorizontal as MoreHorizontalIcon,
  MoreVertical as MoreVerticalIcon,
  ArrowLeft as ArrowLeftIcon,
  ArrowRight as ArrowRightIcon,
  ExternalLink as ExternalLinkIcon,

  // Status icons
  AlertCircle as AlertCircleIcon,
  AlertTriangle as AlertTriangleIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  XCircle as XCircleIcon,

  // App-specific icons
  Dumbbell as DumbbellIcon,
  FlaskConical as FlaskIcon,
  Syringe as SyringeIcon,
  Scale as ScaleIcon,
  Calendar as CalendarIcon,
  CalendarDays as CalendarDaysIcon,
  Clock as ClockIcon,
  Timer as TimerIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Settings as SettingsIcon,
  User as UserIcon,
  LogOut as LogOutIcon,
  Search as SearchIcon,
  Filter as FilterIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,

  // Data/Chart icons
  BarChart3 as BarChartIcon,
  LineChart as LineChartIcon,
  Activity as ActivityIcon,
  Target as TargetIcon,
  Zap as ZapIcon,
  Flame as FlameIcon,
  Heart as HeartIcon,

  // UI icons
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  Lock as LockIcon,
  Unlock as UnlockIcon,
  Star as StarIcon,
  Bell as BellIcon,
  Mail as MailIcon,

  // File/Document icons
  FileText as FileTextIcon,
  Clipboard as ClipboardIcon,
  ClipboardCheck as ClipboardCheckIcon,

  // Misc
  Play as PlayIcon,
  Pause as PauseIcon,
  Square as StopIcon,
  SkipForward as SkipForwardIcon,
  Loader2 as LoaderIcon,
  HelpCircle as HelpIcon,
  Grip as GripIcon,
  GripVertical as GripVerticalIcon,
} from 'lucide-react'

// ============================================
// ICON BUTTON COMPONENT
// ============================================

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon: React.ReactNode
  label: string
  variant?: 'ghost' | 'subtle' | 'danger'
  size?: 'sm' | 'md' | 'lg'
}

export function IconButton({
  icon,
  label,
  variant = 'ghost',
  size = 'md',
  className,
  ...props
}: IconButtonProps) {
  const sizeClasses = {
    sm: 'p-1.5',
    md: 'p-2',
    lg: 'p-3',
  }

  const variantClasses = {
    ghost: 'text-text-muted hover:text-text-primary hover:bg-elevated',
    subtle: 'text-text-secondary hover:text-primary hover:bg-primary/10',
    danger: 'text-text-muted hover:text-error hover:bg-error/10',
  }

  return (
    <button
      className={cn(
        'rounded-lg transition-all duration-150 active:scale-95',
        'focus:outline-none focus:ring-2 focus:ring-primary/50',
        sizeClasses[size],
        variantClasses[variant],
        className
      )}
      aria-label={label}
      title={label}
      {...props}
    >
      {icon}
    </button>
  )
}
